import { message } from 'antd';
import commonApi from 'apis/common';
import LOCALS from 'commons/locals';
import i18n from 'i18n';

export const ALLOWED_RECEIPT_IMAGE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/jpg',
  'image/webp',
] as const;

export type AllowedReceiptImageType =
  (typeof ALLOWED_RECEIPT_IMAGE_TYPES)[number];

export const ALLOWED_RECEIPT_IMAGE_ACCEPT =
  ALLOWED_RECEIPT_IMAGE_TYPES.join(',');

export const isAllowedReceiptImageType = (
  type: string,
): type is AllowedReceiptImageType =>
  (ALLOWED_RECEIPT_IMAGE_TYPES as readonly string[]).includes(type);

/** 上传单张票据图片，返回 object key；类型不合法或失败时返回 null */
export const uploadReceiptImageFile = async (
  file: File,
): Promise<string | null> => {
  if (!isAllowedReceiptImageType(file.type)) {
    message.error(i18n.t(LOCALS.payment_receipt_image_invalid_type));
    return null;
  }

  try {
    const up = await commonApi.uploadFile(file, true, {
      compress: true,
    });
    return up.key;
  } catch {
    return null;
  }
};

/** 唤起文件选择并上传票据图片，返回 object key；取消或失败时返回 null */
export const pickAndUploadReceiptImage = (): Promise<string | null> =>
  new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = ALLOWED_RECEIPT_IMAGE_ACCEPT;

    let settled = false;
    const finish = (result: string | null) => {
      if (settled) return;
      settled = true;
      window.removeEventListener('focus', onWindowFocus);
      resolve(result);
    };

    const onWindowFocus = () => {
      // 等待 change 先触发；若仍未选文件则视为取消
      setTimeout(() => {
        if (!settled && !input.files?.length) {
          finish(null);
        }
      }, 300);
    };

    input.addEventListener('change', async () => {
      const file = input.files?.[0];
      if (!file) {
        finish(null);
        return;
      }
      finish(await uploadReceiptImageFile(file));
    });

    input.addEventListener('cancel', () => {
      finish(null);
    });

    window.addEventListener('focus', onWindowFocus);
    input.click();
  });
